import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  adminApi,
  type AdminPlanRow,
  type AdminPlanDescriptor,
  type AdminPlanLimits,
} from '@/services/adminApi';
import { useRefreshableFetch } from '@/hooks/admin/useRefreshableFetch';
import { AdminListHeader, ActiveFiltersBar } from '@/components/admin/AdminListToolbar';

/**
 * AdminPlansPage — BO-07 plans catalog editor.
 *
 * Two flows:
 *   1. Edit existing plan (static or custom): drawer with all numeric
 *      fields. PATCH validates against PlanDescriptorSchema.
 *   2. Create custom plan: 1-step modal restricted to agency/enterprise
 *      with mandatory `scopeAgencyId`.
 *
 * Soft delete = `deprecated=true` — only blocks new checkouts; existing
 * subscriptions keep paying.
 */
export default function AdminPlansPage() {
  const [segment, setSegment] = useState('all');
  const [isCustom, setIsCustom] = useState('all');
  const [includeDeprecated, setIncludeDeprecated] = useState(false);
  const [searchInline, setSearchInline] = useState('');

  const filterParams = useMemo(() => ({
    ...(segment !== 'all' ? { segment } : {}),
    ...(isCustom !== 'all' ? { isCustom: isCustom === 'true' } : {}),
    ...(includeDeprecated ? { includeDeprecated: true } : {}),
  }), [segment, isCustom, includeDeprecated]);

  const { data, loading, isRefreshing, error, refresh } = useRefreshableFetch(
    () => adminApi.plans.list(filterParams),
    [segment, isCustom, includeDeprecated],
  );
  const items: AdminPlanRow[] = data?.items ?? [];

  const filtered = useMemo(() => {
    if (!searchInline) return items;
    const s = searchInline.toLowerCase();
    return items.filter((r) =>
      r.planId.toLowerCase().includes(s)
      || r.segment.toLowerCase().includes(s)
      || (r.scopeAgencyId ?? '').toLowerCase().includes(s),
    );
  }, [items, searchInline]);

  const [editTarget, setEditTarget] = useState<AdminPlanRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDeprecate, setPendingDeprecate] = useState<AdminPlanRow | null>(null);

  const activeFilters: Array<{ key: string; label: string; onClear: () => void }> = [];
  if (segment !== 'all') activeFilters.push({ key: 'seg', label: `Segment: ${segment}`, onClear: () => setSegment('all') });
  if (isCustom !== 'all') activeFilters.push({ key: 'cust', label: isCustom === 'true' ? 'Custom only' : 'Static only', onClear: () => setIsCustom('all') });
  if (includeDeprecated) activeFilters.push({ key: 'dep', label: 'Include deprecated', onClear: () => setIncludeDeprecated(false) });
  if (searchInline) activeFilters.push({ key: 'q', label: `Search: "${searchInline}"`, onClear: () => setSearchInline('') });

  const clearAll = () => {
    setSegment('all'); setIsCustom('all'); setIncludeDeprecated(false); setSearchInline('');
  };

  return (
    <div className="space-y-4 max-w-6xl">
      <AdminListHeader
        title="Plans catalog"
        onRefresh={refresh}
        isRefreshing={isRefreshing}
        right={
          <Button size="sm" className="gap-1.5" onClick={() => setCreateOpen(true)}>
            <Plus className="h-3.5 w-3.5" /> Create custom plan
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search loaded rows (id, segment, scope)..."
          value={searchInline}
          onChange={(e) => setSearchInline(e.target.value)}
          className="h-9 w-[280px]"
        />
        <Select value={segment} onValueChange={setSegment}>
          <SelectTrigger className="h-9 w-[140px]"><SelectValue placeholder="Segment" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All segments</SelectItem>
            <SelectItem value="sme">SME</SelectItem>
            <SelectItem value="agency">Agency</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
        <Select value={isCustom} onValueChange={setIsCustom}>
          <SelectTrigger className="h-9 w-[150px]"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            <SelectItem value="false">Static only</SelectItem>
            <SelectItem value="true">Custom only</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 ml-1">
          <Switch id="incl-deprecated" checked={includeDeprecated} onCheckedChange={setIncludeDeprecated} />
          <Label htmlFor="incl-deprecated" className="text-sm cursor-pointer">Include deprecated</Label>
        </div>
      </div>

      <ActiveFiltersBar filters={activeFilters} onClearAll={clearAll} />

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card className="border-border/50">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Plan</th>
                  <th className="text-left py-3 px-3 font-medium text-muted-foreground">Segment</th>
                  <th className="text-right py-3 px-3 font-medium text-muted-foreground">Monthly</th>
                  <th className="text-right py-3 px-3 font-medium text-muted-foreground">Annual</th>
                  <th className="text-right py-3 px-3 font-medium text-muted-foreground">Companies</th>
                  <th className="text-left py-3 px-3 font-medium text-muted-foreground">Limits</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="py-12 text-center text-muted-foreground">Loading...</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={7} className="py-12 text-center text-muted-foreground">No plans match.</td></tr>
                )}
                {filtered.map((row) => (
                  <tr key={row.planId} className="border-b border-border/30 last:border-0 hover:bg-accent/40">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs">{row.planId}</span>
                        {row.isCustom && <Badge variant="secondary" className="text-xs">custom</Badge>}
                        {row.deprecated && <Badge variant="destructive" className="text-xs">deprecated</Badge>}
                      </div>
                      {row.scopeAgencyId && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Scope: <Link to={`/admin/users/${row.scopeAgencyId}`} className="hover:underline">{row.scopeAgencyId.slice(0, 12)}…</Link>
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-3 capitalize text-muted-foreground">{row.segment}</td>
                    <td className="py-3 px-3 text-right tabular-nums">€ {row.priceMonthly}</td>
                    <td className="py-3 px-3 text-right tabular-nums">{row.hasAnnual ? `€ ${row.priceAnnual ?? '—'}` : '—'}</td>
                    <td className="py-3 px-3 text-right tabular-nums">
                      <Link to={`/admin/customers?plan=${row.planId}`} className="hover:underline">
                        {row.companiesCount}
                      </Link>
                    </td>
                    <td className="py-3 px-3 text-xs text-muted-foreground">
                      {row.limits.seats}s · {row.limits.positions}p · {row.limits.candidates}c · {row.limits.aiCompany}ai
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex gap-1.5">
                        <Button size="sm" variant="ghost" onClick={() => setEditTarget(row)}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        {!row.deprecated && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setPendingDeprecate(row)}
                            className="text-destructive hover:bg-destructive/10"
                            title="Deprecate (soft delete)"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-border/50 p-3 text-center text-xs text-muted-foreground">
            {filtered.length === items.length
              ? <>{items.length} plans</>
              : <>{filtered.length} of {items.length} shown (filtered)</>}
          </div>
        </CardContent>
      </Card>

      {/* Edit drawer */}
      <EditPlanDialog
        open={Boolean(editTarget)}
        onOpenChange={(o) => !o && setEditTarget(null)}
        plan={editTarget}
        onSaved={() => { setEditTarget(null); refresh(); }}
      />

      {/* Create custom plan */}
      <CreatePlanDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => { setCreateOpen(false); refresh(); }}
      />

      {/* Deprecate confirm */}
      <AlertDialog open={Boolean(pendingDeprecate)} onOpenChange={(o) => !o && setPendingDeprecate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" /> Deprecate plan {pendingDeprecate?.planId}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              New checkouts on this plan will be blocked. Existing subscriptions ({pendingDeprecate?.companiesCount ?? 0} companies) keep their billing intact until they choose a different plan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <DeprecateForm
            onConfirm={async (reason) => {
              if (!pendingDeprecate) return;
              await adminApi.plans.deprecate(pendingDeprecate.planId, reason);
              toast.success(`${pendingDeprecate.planId} deprecated`);
              setPendingDeprecate(null);
              refresh();
            }}
            onCancel={() => setPendingDeprecate(null)}
          />
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit plan dialog
// ---------------------------------------------------------------------------

function EditPlanDialog({
  open, onOpenChange, plan, onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  plan: AdminPlanRow | null;
  onSaved: () => void;
}) {
  const [priceMonthly, setPriceMonthly] = useState(0);
  const [priceAnnual, setPriceAnnual] = useState(0);
  const [hasAnnual, setHasAnnual] = useState(false);
  const [limits, setLimits] = useState<AdminPlanLimits | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Hydrate when the dialog opens
  useMemo(() => {
    if (plan && open) {
      setPriceMonthly(plan.priceMonthly);
      setPriceAnnual(plan.priceAnnual ?? 0);
      setHasAnnual(plan.hasAnnual);
      setLimits({ ...plan.limits });
      setReason('');
    }
  }, [plan, open]);

  if (!plan || !limits) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit plan</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </DialogContent>
      </Dialog>
    );
  }

  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    try {
      const patch: Partial<AdminPlanDescriptor> = {
        pricing: { priceMonthly, hasAnnual, ...(hasAnnual ? { priceAnnual } : {}) },
        limits,
      };
      await adminApi.plans.update(plan.planId, patch, reason.trim());
      toast.success(`${plan.planId} updated`);
      onSaved();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit {plan.planId}</DialogTitle>
          <DialogDescription>
            Existing subscriptions keep paying their original price. New checkouts use the updated values.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="pm">Price monthly (€)</Label>
            <Input id="pm" type="number" min={0} value={priceMonthly} onChange={(e) => setPriceMonthly(Number(e.target.value))} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pa">Price annual (€)</Label>
            <Input id="pa" type="number" min={0} value={priceAnnual} onChange={(e) => setPriceAnnual(Number(e.target.value))} disabled={!hasAnnual} />
          </div>
          <div className="flex items-center gap-2 col-span-2">
            <Switch id="ha" checked={hasAnnual} onCheckedChange={setHasAnnual} />
            <Label htmlFor="ha" className="cursor-pointer">Has annual variant</Label>
          </div>
        </div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mt-4">Limits</h3>
        <div className="grid grid-cols-3 gap-3">
          {/* v3.5: aiUser pool eliminato — non più editabile in admin */}
          {(['seats', 'positions', 'candidates', 'aiCompany', 'transcripts'] as const).map((k) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={`lim-${k}`}>{k}</Label>
              <Input
                id={`lim-${k}`}
                type="number"
                value={(limits as unknown as Record<string, number>)[k] ?? 0}
                onChange={(e) => setLimits({ ...limits, [k]: Number(e.target.value) })}
              />
            </div>
          ))}
          {plan.segment === 'agency' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="lim-ac">Agency clients</Label>
                <Input id="lim-ac" type="number" value={limits.agencyClients ?? 0} onChange={(e) => setLimits({ ...limits, agencyClients: Number(e.target.value) })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lim-rs">Recruiter seats min</Label>
                <Input id="lim-rs" type="number" value={limits.recruiterSeatsMin ?? 0} onChange={(e) => setLimits({ ...limits, recruiterSeatsMin: Number(e.target.value) })} />
              </div>
            </>
          )}
        </div>
        <div className="space-y-1.5 mt-4">
          <Label htmlFor="ed-reason">Reason <span className="text-destructive">*</span></Label>
          <Textarea id="ed-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !reason.trim()}>{busy ? 'Saving...' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Create custom plan
// ---------------------------------------------------------------------------

function CreatePlanDialog({
  open, onOpenChange, onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: () => void;
}) {
  const [planId, setPlanId] = useState('agency_starter_');
  const [segment, setSegment] = useState<'agency' | 'enterprise'>('agency');
  const [scopeAgencyId, setScopeAgencyId] = useState('');
  const [priceMonthly, setPriceMonthly] = useState(199);
  const [priceAnnual, setPriceAnnual] = useState(2000);
  const [hasAnnual, setHasAnnual] = useState(true);
  const [agencyClients, setAgencyClients] = useState(5);
  const [recruiterSeatsMin, setRecruiterSeatsMin] = useState(1);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset state on open
  useMemo(() => {
    if (open) {
      setPlanId('agency_starter_');
      setSegment('agency');
      setScopeAgencyId('');
      setPriceMonthly(199);
      setPriceAnnual(2000);
      setHasAnnual(true);
      setAgencyClients(5);
      setRecruiterSeatsMin(1);
      setReason('');
    }
  }, [open]);

  const canSubmit = !!reason.trim()
    && !!scopeAgencyId.trim()
    && /^agency_(starter|professional|enterprise)_[a-z0-9_-]+$/.test(planId)
    && priceMonthly > 0;

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      const descriptor: AdminPlanDescriptor = {
        segment,
        limits: {
          seats: 999_999,
          positions: 50,
          candidates: 999_999,
          aiCompany: 999_999,
          // v3.5: aiUser pool eliminato — sempre 0 (optional)
          transcripts: 30,
          agencyClients,
          recruiterSeatsMin,
        },
        pricing: { priceMonthly, hasAnnual, ...(hasAnnual ? { priceAnnual } : {}) },
        isCustom: true,
        scopeAgencyId: scopeAgencyId.trim(),
      };
      await adminApi.plans.create(planId, descriptor, reason.trim());
      toast.success(`${planId} created`);
      onCreated();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create custom plan</DialogTitle>
          <DialogDescription>
            Used for negotiated agency/enterprise contracts. Visible only to the target agency owner.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5 col-span-2">
            <Label htmlFor="pid">Plan id</Label>
            <Input
              id="pid"
              value={planId}
              onChange={(e) => setPlanId(e.target.value.toLowerCase())}
              placeholder="agency_starter_acme"
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Pattern: <code>agency_{`{starter|professional|enterprise}`}_{`{slug}`}</code>
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="seg">Segment</Label>
            <Select value={segment} onValueChange={(v) => setSegment(v as 'agency' | 'enterprise')}>
              <SelectTrigger id="seg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="agency">Agency</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="scope">Scope agency uid</Label>
            <Input id="scope" value={scopeAgencyId} onChange={(e) => setScopeAgencyId(e.target.value)} placeholder="user uid of the target agency owner" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cpm">Price monthly (€)</Label>
            <Input id="cpm" type="number" min={1} value={priceMonthly} onChange={(e) => setPriceMonthly(Number(e.target.value))} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cpa">Price annual (€)</Label>
            <Input id="cpa" type="number" min={0} value={priceAnnual} onChange={(e) => setPriceAnnual(Number(e.target.value))} disabled={!hasAnnual} />
          </div>
          <div className="flex items-center gap-2 col-span-2">
            <Switch id="cha" checked={hasAnnual} onCheckedChange={setHasAnnual} />
            <Label htmlFor="cha" className="cursor-pointer">Has annual variant</Label>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ac">Agency clients cap</Label>
            <Input id="ac" type="number" min={1} value={agencyClients} onChange={(e) => setAgencyClients(Number(e.target.value))} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rsm">Recruiter seats min</Label>
            <Input id="rsm" type="number" min={1} value={recruiterSeatsMin} onChange={(e) => setRecruiterSeatsMin(Number(e.target.value))} />
          </div>
        </div>
        <div className="space-y-1.5 mt-4">
          <Label htmlFor="cr-reason">Reason <span className="text-destructive">*</span></Label>
          <Textarea id="cr-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !canSubmit}>{busy ? 'Creating...' : 'Create plan'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeprecateForm({
  onConfirm,
  onCancel,
}: {
  onConfirm: (reason: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="dep-reason">Reason <span className="text-destructive">*</span></Label>
        <Textarea id="dep-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
      </div>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={busy} onClick={onCancel}>Cancel</AlertDialogCancel>
        <AlertDialogAction
          disabled={busy || !reason.trim()}
          onClick={async (e) => {
            e.preventDefault();
            setBusy(true);
            try { await onConfirm(reason.trim()); }
            finally { setBusy(false); }
          }}
          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
        >
          {busy ? 'Deprecating...' : 'Deprecate'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  );
}
